import { useEffect, useState } from "react";
import type { VideoMilestone, VideoProgress } from "../api/types.js";
import { formatElapsed, formatRelative } from "../lib/time-format.js";
import { MilestoneStrip } from "./MilestoneStrip.js";

/**
 * P0 UX fix (2026-09): Gate 2 progress-bar/milestone parity with Gate 1's
 * `PersonaSourceStillProgressCard` — same doctrine (durable-state-only
 * milestones, wall-clock ELAPSED label only, stuck detection on staleness,
 * never a fabricated percentage). Renders EITHER the pre-review checklist
 * (`milestones.preReview`) or the post-approval one (`milestones.finalizing`)
 * — the caller picks which, since 100% "Gate 2 ready" must never be
 * confused with 100% "final video finished".
 *
 * Truthful freshness correction (2026-09-07, finding #2) — same fix as
 * `PersonaSourceStillProgressCard`'s own top doc comment: `progress.updatedAt`
 * is durable-state-change time (`update-video-processing-job-from-provider.ts`'s
 * `computeStatusAfterProviderSignal` genuinely no-ops, no save at all,
 * whenever the provider reports the same non-terminal status again), not
 * "last successful poll" — a healthy, actively-15s-polled job could show
 * a stale "no update in 2 minutes" purely because nothing changed yet.
 * `lastCheckedAtMs` is the caller's own client-tracked `Date.now()` at its
 * last successful poll (never persisted); `updatedAt` keeps its original
 * meaning and still solely drives the stuck-detector below.
 */
const STUCK_THRESHOLD_MS = 5 * 60 * 1000;
/**
 * FIX-061 t517u (2): the provider is still rendering when its last SUCCESSFUL poll (`providerLastPolledAt`, a throttled server heartbeat, ~1/min) is
 * younger than this. Then a long gap since the last durable update is NOT a stall: the card says "Seedance still rendering" instead of
 * "PROCESSING DELAYED". When polls stop succeeding the heartbeat ages past this window and the real stuck banner returns.
 */
const PROVIDER_HEARTBEAT_FRESH_MS = 3 * 60 * 1000;

/** FIX-061 t517u (2): exported for tests. True when the provider is demonstrably still working (fresh heartbeat while submitted/processing). */
export function isProviderStillRendering(progress: Pick<VideoProgress, "providerStatus" | "providerLastPolledAt">, nowMs: number): boolean {
  if (progress.providerStatus !== "submitted" && progress.providerStatus !== "processing") return false;
  const raw = progress.providerLastPolledAt ?? null;
  if (raw === null) return false;
  const polledMs = Date.parse(raw);
  return Number.isFinite(polledMs) && nowMs - polledMs <= PROVIDER_HEARTBEAT_FRESH_MS && polledMs - nowMs <= 60_000;
}

/**
 * FIX-064 speech gate (2026-10-22): `upscaling` stays the video status after Gate 2 approval until the subtitle burn / promotion, even when the upscale is
 * long finished (SP-1 starts it at Gate 2 ready) and only the speech check / transcription / translation are running. The raw "UPSCALING" badge then
 * read as "approval not taken". Display text only — the status, its CSS class and every bucket filter are unchanged. Exported for tests.
 */
export function videoStatusBadgeText(progress: Pick<VideoProgress, "videoStatus" | "progressStage">): string {
  if (progress.videoStatus === "upscaling" && progress.progressStage !== "upscaling") {
    return "FINALIZING";
  }
  return progress.videoStatus.replace(/_/g, " ").toUpperCase();
}

function useNowTicking(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

export function VideoGateProgressCard({
  title,
  progress,
  milestones,
  startedAtIso,
  lastCheckedAtMs,
}: {
  readonly title: string;
  readonly progress: VideoProgress;
  readonly milestones: readonly VideoMilestone[];
  /** `videoCreatedAt` for the pre-review phase, or the approval timestamp for finalizing — always a real persisted timestamp, never a frontend-only start time. */
  readonly startedAtIso: string;
  /** The caller's own `Date.now()` at its last SUCCESSFUL poll of this data — see this file's own top doc comment. `null` only in the (practically unreachable) case a caller renders this card before its own first successful fetch ever resolved. */
  readonly lastCheckedAtMs: number | null;
}) {
  const now = useNowTicking(1000);
  const startedAtMs = new Date(startedAtIso).getTime();
  const updatedAtMs = new Date(progress.updatedAt).getTime();
  const elapsedMs = now - startedAtMs;
  const sinceDurableUpdateMs = now - updatedAtMs; // stuck-detector input only — never displayed directly, see top doc comment
  const sinceCheckedMs = lastCheckedAtMs !== null ? now - lastCheckedAtMs : null;
  const isNonTerminal = progress.videoStatus !== "finalized" && progress.videoStatus !== "rejected" && progress.videoStatus !== "failed";
  const staleDurableState = isNonTerminal && sinceDurableUpdateMs > STUCK_THRESHOLD_MS;
  const providerStillRendering = staleDurableState && isProviderStillRendering(progress, now);
  const isStuck = staleDurableState && !providerStillRendering;
  const trackedCost = progress.costSummary.totalActualCostUsd ?? progress.costSummary.totalEstimatedCostUsd;

  return (
    <div className="progress-card">
      <div className="progress-header">
        <span className="progress-stage-label">{title}</span>
        <span className={`badge status-${progress.videoStatus}`}>{videoStatusBadgeText(progress)}</span>
      </div>

      <MilestoneStrip milestones={milestones} stuck={isStuck} />

      {isStuck ? (
        <div className="stuck-banner">PROCESSING DELAYED — no durable update in {formatRelative(sinceDurableUpdateMs)}</div>
      ) : providerStillRendering ? (
        <p className="muted" role="status" data-testid="gate2-provider-still-rendering">
          SEEDANCE STILL RENDERING — the provider is still working on this video (last checked {formatRelative(now - Date.parse(progress.providerLastPolledAt ?? ""))}). This is normal for a slow provider queue; the card updates by itself.
        </p>
      ) : progress.failureDetail ? (
        <div className="stuck-banner">
          NEEDS ATTENTION: {progress.failureDetail.failureStage} — {progress.failureDetail.failureReason}
        </div>
      ) : null}

      <div className="progress-meta">
        <div>
          <div className="label">Current stage</div>
          <div className="value">{progress.progressStage}</div>
        </div>
        <div>
          <div className="label">Elapsed</div>
          <div className="value">{formatElapsed(elapsedMs)}</div>
        </div>
        <div>
          <div className="label">Last checked</div>
          <div className="value">{sinceCheckedMs !== null ? formatRelative(sinceCheckedMs) : "—"}</div>
        </div>
        <div>
          <div className="label">Provider status</div>
          <div className="value">{progress.providerStatus ?? "—"}</div>
        </div>
        <div>
          <div className="label">Tracked cost</div>
          <div className="value">${(trackedCost ?? 0).toFixed(2)}</div>
        </div>
        {progress.costSummary.completeness === "untracked_google_cost" && (
          <div>
            <div className="label">Cost note</div>
            <div className="value">excludes untracked Google Cloud usage</div>
          </div>
        )}
      </div>
    </div>
  );
}
